import { useState, useCallback, useEffect, useRef } from "react";
import {
  Typography,
  Alert,
  CircularProgress,
  Tooltip,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import { API_CONFIG } from "../config/api";
import ChatMessage from "../components/ChatMessage";
import { normalizeMarkdown } from "../utils/normalizeMarkdown";
import ChatInput from "../components/ChatInput";
import ChatSidebar from "../components/ChatSidebar";
import DrWeedyLogo from "../assets/DrWeedyLogo";
import { useAppAnalytics } from "../utils/useAppAnalytics";

interface Message {
  id: string;
  text: string;
  from: "user" | "ai";
  created_at: string;
}

interface ChatSession {
  session_id: string;
  title: string;
  first_message?: string;
  created_at: string;
  messages?: Message[];
}

export default function ChatPage() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { trackEvent, resetUser } = useAppAnalytics({ user });

  const handleLogout = useCallback(async () => {
    await trackEvent("logout");
    resetUser();
    logout();
  }, [trackEvent, resetUser, logout]);

  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string>("");
  const [currentMessages, setCurrentMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState("");
  const [streamingEnabled, setStreamingEnabled] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingProgress, setPollingProgress] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isInputCollapsed, setIsInputCollapsed] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{
    route: string;
    agentsCalled: string[];
    // Status real por agent (evento "progress"), não sintético - "pending"
    // até o agent especializado correspondente terminar.
    agentStatus: Record<string, "pending" | "ok" | "warn">;
  } | null>(null);

  // Trilha do que o sistema já decidiu enquanto os agentes não voltam. Os
  // agentes levam 12,9-19,2s e a síntese outros 15,0s (medido); sem isto o
  // médico vê só chips nesse intervalo.
  const [reasoning, setReasoning] = useState<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const currentChat = chatSessions.find(
    (chat) => chat.session_id === currentChatId,
  );

  useEffect(() => {
    loadChatSessions();
  }, []);

  useEffect(() => {
    if (currentChatId) {
      loadChatMessages(currentChatId);
    } else {
      setCurrentMessages([]);
    }
  }, [currentChatId]);

  const scrollToBottom = (smooth: boolean = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
    });
  };

  useEffect(() => {
    if (currentMessages.length > 0 && !isStreaming && !sendingMessage) {
      scrollToBottom(true);
    }
  }, [currentChatId]);

  useEffect(() => {
    if (currentMessages.length > 0 && sendingMessage && !isStreaming) {
      scrollToBottom(false);
    }
  }, [sendingMessage]);

  const handleMainScroll = useCallback(() => {
    const el = mainRef.current;
    if (!el || sendingMessage || isStreaming || isPolling) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > 150) setIsInputCollapsed(true);
    else if (distanceFromBottom < 50) setIsInputCollapsed(false);
  }, [sendingMessage, isStreaming, isPolling]);

  useEffect(() => {
    if (sendingMessage || isStreaming) setIsInputCollapsed(false);
  }, [sendingMessage, isStreaming]);

  const loadChatSessions = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiService.getSessions();
      setChatSessions(response.data);

      if (response.data.length > 0 && !currentChatId) {
        setCurrentChatId(response.data[0].session_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar sessões");
    } finally {
      setLoading(false);
    }
  };

  const loadChatMessages = async (sessionId: string) => {
    try {
      setLoading(true);
      const response = await apiService.getSessionHistory(sessionId);
      const messages = response.data.messages.map((msg) => ({
        id: msg.id,
        text: msg.message,
        from: msg.source as "user" | "ai",
        created_at: msg.created_at,
      }));
      setCurrentMessages(messages);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar mensagens",
      );
      setCurrentMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const createNewChat = useCallback(() => {
    setCurrentChatId("");
    setCurrentMessages([]);
  }, []);

  const selectChat = useCallback(async (chatId: string) => {
    setCurrentChatId(chatId);
  }, []);

  // Recupera uma resposta que ainda não chegou pelo canal ao vivo (SSE parou
  // por inatividade, ou o POST do modo BATCH estourou o timeout) buscando o
  // histórico da sessão até a IA terminar de salvar a resposta no banco — em
  // vez de desistir e deixar o usuário sem saber que precisa dar refresh.
  const recoverPendingResponse = async (
    sessionId: string,
    initialMessageCount: number,
  ) => {
    if (!sessionId) {
      setError("Não foi possível localizar sua conversa. Tente novamente.");
      return;
    }

    setIsPolling(true);
    setPollingProgress(0);

    const pollProgressIncrement =
      100 / (API_CONFIG.MAX_POLL_DURATION_MS / 2000);
    const progressInterval = setInterval(() => {
      setPollingProgress((prev) => Math.min(prev + pollProgressIncrement, 95));
    }, 2000);

    try {
      const messages = await apiService.pollForResponse(
        sessionId,
        initialMessageCount,
      );

      if (messages && messages.length > initialMessageCount) {
        setPollingProgress(100);
        const newMessages = messages.slice(initialMessageCount).map((msg) => ({
          id: msg.id,
          text: msg.message,
          from: msg.source as "user" | "ai",
          created_at: msg.created_at,
        }));

        setCurrentMessages((prev) => [...prev, ...newMessages]);
      } else {
        setError(
          "Isso está demorando muito mais que o normal, mas sua consulta pode ainda estar sendo processada. Se a resposta não aparecer em alguns minutos, tente perguntar novamente.",
        );
      }
    } catch {
      setError(
        "Isso está demorando muito mais que o normal, mas sua consulta pode ainda estar sendo processada. Se a resposta não aparecer em alguns minutos, tente perguntar novamente.",
      );
    } finally {
      clearInterval(progressInterval);
      setIsPolling(false);
      setPollingProgress(0);
    }
  };

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: text,
      from: "user",
      created_at: new Date().toISOString(),
    };
    setCurrentMessages((prev) => [...prev, userMessage]);
    const initialMessageCount = currentMessages.length + 1;

    if (streamingEnabled) {
      setSendingMessage(true);
      setError("");
      setIsStreaming(true);
      setStreamingMessage("");

      let currentSessionId = currentChatId;
      let fullAiMessage = "";
      let renderBuffer = "";
      let renderTimeout: NodeJS.Timeout | null = null;
      let hasAppendedFinalMessage = false;

      const smartRender = (content: string) => {
        const lines = content.split("\n");
        let safeContent = "";
        let i = 0;

        while (i < lines.length) {
          const line = lines[i];

          if (line.trim().startsWith("|")) {
            if (line.trim().endsWith("|")) {
              safeContent += line + "\n";
            } else {
              break;
            }
          } else if (line.match(/^```/)) {
            const codeBlockLines = [line];
            let j = i + 1;
            let foundClosing = false;

            while (j < lines.length) {
              codeBlockLines.push(lines[j]);
              if (lines[j].match(/^```/)) {
                foundClosing = true;
                i = j;
                break;
              }
              j++;
            }

            if (foundClosing) {
              safeContent += codeBlockLines.join("\n") + "\n";
            } else {
              break;
            }
          } else {
            safeContent += line;
            if (i < lines.length - 1) {
              safeContent += "\n";
            }
          }

          i++;
        }

        return safeContent;
      };

      const scheduleRender = () => {
        if (renderTimeout) {
          clearTimeout(renderTimeout);
        }

        renderTimeout = setTimeout(() => {
          const safeContent = smartRender(normalizeMarkdown(renderBuffer));
          setStreamingMessage(safeContent);
        }, 100);
      };

      try {
        await apiService.sendMessageStream(
          {
            sessionId: currentChatId || undefined,
            message: text,
          },
          (event) => {
            console.log("[CHATPAGE] Received event:", event.type);
            switch (event.type) {
              case "session_info":
                console.log("Session started:", event.sessionId);
                if (event.sessionId && !currentSessionId) {
                  currentSessionId = event.sessionId;
                  setCurrentChatId(event.sessionId);
                }
                break;

              case "route_info": {
                console.log(
                  "[CHATPAGE] Route info:",
                  event.route,
                  event.agentsCalled,
                );
                const agentsCalled: string[] = event.agentsCalled || [];
                setReasoning([]);
                setRouteInfo({
                  route: event.route || "normal",
                  agentsCalled,
                  agentStatus: Object.fromEntries(
                    agentsCalled.map((agent) => [agent, "pending"]),
                  ),
                });
                break;
              }

              case "reasoning": {
                if (event.text) {
                  setReasoning((prev) =>
                    prev.includes(event.text!) ? prev : [...prev, event.text!],
                  );
                }
                break;
              }

              case "progress": {
                console.log(
                  "[CHATPAGE] Agent progress:",
                  event.agent,
                  event.status,
                );
                const { agent, status } = event;
                if (agent && status) {
                  setRouteInfo((prev) =>
                    prev
                      ? {
                          ...prev,
                          agentStatus: { ...prev.agentStatus, [agent]: status },
                        }
                      : prev,
                  );
                }
                break;
              }

              case "token":
                if (event.token) {
                  fullAiMessage += event.token;
                  renderBuffer = fullAiMessage;
                  scheduleRender();
                }
                break;

              case "done":
                console.log(
                  "[CHATPAGE] Received done event, forcing completion",
                );

                if (renderTimeout) {
                  clearTimeout(renderTimeout);
                  renderTimeout = null;
                }

                if (event.fullMessage) {
                  fullAiMessage = event.fullMessage;
                }

                renderBuffer = fullAiMessage;
                setStreamingMessage(fullAiMessage);

                if (fullAiMessage.trim() && !hasAppendedFinalMessage) {
                  hasAppendedFinalMessage = true;
                  const finalAiMessage: Message = {
                    id: `ai-${Date.now()}`,
                    text: fullAiMessage,
                    from: "ai",
                    created_at: new Date().toISOString(),
                  };

                  console.log(
                    "[CHATPAGE] Done: Adding final AI message to chat",
                  );
                  setCurrentMessages((prev) => [...prev, finalAiMessage]);
                }

                console.log("[CHATPAGE] Done: Clearing streaming states");
                setIsStreaming(false);
                setStreamingMessage("");
                setSendingMessage(false);
                setRouteInfo(null);
                setReasoning([]);
                break;

              case "complete":
                console.log(
                  "[CHATPAGE] Received complete event:",
                  event.processingTimeMs
                    ? event.processingTimeMs + "ms"
                    : "timeout",
                );
                console.log(
                  "[CHATPAGE] fullAiMessage length:",
                  fullAiMessage.length,
                );
                console.log("[CHATPAGE] Clearing all states and timeouts");

                if (renderTimeout) {
                  clearTimeout(renderTimeout);
                  renderTimeout = null;
                }

                if (!fullAiMessage.trim() && event.aiMessage) {
                  fullAiMessage = event.aiMessage;
                }

                if (fullAiMessage.trim() && !hasAppendedFinalMessage) {
                  hasAppendedFinalMessage = true;
                  const finalAiMessage: Message = {
                    id: `ai-${Date.now()}`,
                    text: fullAiMessage,
                    from: "ai",
                    created_at: new Date().toISOString(),
                  };

                  console.log("[CHATPAGE] Adding final AI message to chat");
                  setCurrentMessages((prev) => [...prev, finalAiMessage]);
                } else if (hasAppendedFinalMessage) {
                  console.log("[CHATPAGE] Final AI message already added.");
                } else {
                  // Nada chegou antes do timeout de inatividade do SSE (20s) —
                  // o backend pode ainda estar processando (ex.: orquestração
                  // multi-agente). Recupera pelo histórico em vez de desistir
                  // em silêncio, igual ao fallback do modo BATCH.
                  console.warn(
                    "[CHATPAGE] No message content before stream timeout, falling back to polling",
                  );
                  hasAppendedFinalMessage = true;
                  recoverPendingResponse(currentSessionId, initialMessageCount);
                }

                console.log(
                  "[CHATPAGE] Setting isStreaming=false, clearing streamingMessage",
                );
                setIsStreaming(false);
                setStreamingMessage("");
                setSendingMessage(false);
                setRouteInfo(null);
                setReasoning([]);

                if (currentSessionId) {
                  setChatSessions((prev) => {
                    const existingIndex = prev.findIndex(
                      (s) => s.session_id === currentSessionId,
                    );

                    const updatedSession: ChatSession = {
                      session_id: currentSessionId,
                      title: `Chat ${new Date().toLocaleString()}`,
                      created_at: new Date().toISOString(),
                      first_message: text,
                    };

                    if (existingIndex >= 0) {
                      const newSessions = [...prev];
                      newSessions[existingIndex] = updatedSession;
                      return newSessions;
                    } else {
                      return [updatedSession, ...prev];
                    }
                  });
                }
                break;

              case "error":
                console.error("Stream error:", event.error);

                if (renderTimeout) {
                  clearTimeout(renderTimeout);
                  renderTimeout = null;
                }

                setError(event.error || "Erro durante o streaming");
                setIsStreaming(false);
                setStreamingMessage("");
                setSendingMessage(false);
                setRouteInfo(null);
                setReasoning([]);
                break;
            }
          },
        );
      } catch (err) {
        if (renderTimeout) {
          clearTimeout(renderTimeout);
        }

        const errorMessage =
          err instanceof Error ? err.message : "Erro ao enviar mensagem";
        setError(errorMessage);
        setIsStreaming(false);
        setStreamingMessage("");
        setSendingMessage(false);
        setRouteInfo(null);
        setReasoning([]);
      }
    } else {
      try {
        setSendingMessage(true);
        setError("");
        setIsPolling(false);
        setPollingProgress(0);

        const response = await apiService.sendMessage({
          sessionId: currentChatId || undefined,
          message: text,
        });

        const aiMessage: Message = {
          id: `ai-${Date.now()}`,
          text: response.data.aiMessage,
          from: "ai",
          created_at: new Date().toISOString(),
        };

        setCurrentMessages((prev) => [...prev, aiMessage]);

        const sessionData = response.data.session;
        setChatSessions((prev) => {
          const existingIndex = prev.findIndex(
            (s) => s.session_id === sessionData.session_id,
          );
          const updatedSession: ChatSession = {
            session_id: sessionData.session_id,
            title: sessionData.title,
            created_at: sessionData.created_at,
            first_message: text,
          };

          if (existingIndex >= 0) {
            const newSessions = [...prev];
            newSessions[existingIndex] = updatedSession;
            return newSessions;
          } else {
            return [updatedSession, ...prev];
          }
        });

        if (!currentChatId) {
          setCurrentChatId(sessionData.session_id);
        }

        setSendingMessage(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Erro ao enviar mensagem";

        if (errorMessage === "timeout") {
          setSendingMessage(false);
          recoverPendingResponse(currentChatId || "", initialMessageCount);
        } else {
          setSendingMessage(false);
          setError(errorMessage);
        }
      }
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      await apiService.deleteSession(sessionId);
      setChatSessions((prev) => prev.filter((s) => s.session_id !== sessionId));

      if (currentChatId === sessionId) {
        const remainingSessions = chatSessions.filter(
          (s) => s.session_id !== sessionId,
        );
        if (remainingSessions.length > 0) {
          setCurrentChatId(remainingSessions[0].session_id);
        } else {
          setCurrentChatId("");
          setCurrentMessages([]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao deletar sessão");
    }
  };

  const copyMessagesToClipboard = async () => {
    try {
      const messagesToCopy = currentMessages
        .map((m) => {
          const sender = m.from === "user" ? "Você" : "Dr. Weedy";
          return `${sender}:\n${m.text}\n`;
        })
        .join("\n");

      await navigator.clipboard.writeText(messagesToCopy);
      setMenuAnchor(null);
      setError("");

      const tempAlert = document.createElement("div");
      tempAlert.className =
        "fixed top-4 right-4 z-50 bg-saluton/90 text-white px-6 py-3 rounded-2xl shadow-xl";
      tempAlert.textContent = "Conversas copiadas!";
      document.body.appendChild(tempAlert);
      setTimeout(() => tempAlert.remove(), 3000);
    } catch (err) {
      setError("Erro ao copiar mensagens");
    }
  };

  const exportToPDF = async () => {
    try {
      setMenuAnchor(null);

      if (!chatContainerRef.current) return;

      const WRAPPER_WIDTH_PX = 1200;
      const WRAPPER_PAD_X_PX = 40;
      const PAGE_W_MM = 210;
      const PAGE_H_MM = 297;

      const makeBlockWrapper = () => {
        const w = document.createElement("div");
        w.style.position = "fixed";
        w.style.left = "-9999px";
        w.style.top = "0";
        w.style.width = `${WRAPPER_WIDTH_PX}px`;
        w.style.background = "#0a1a21";
        w.style.padding = `30px ${WRAPPER_PAD_X_PX}px`;
        w.style.boxSizing = "border-box";
        return w;
      };

      const applyTableWrapOverrides = (root: HTMLElement) => {
        root.querySelectorAll<HTMLElement>("table").forEach((t) => {
          t.style.tableLayout = "fixed";
          t.style.width = "100%";
          t.style.minWidth = "0";
        });
        root.querySelectorAll<HTMLElement>("th, td").forEach((c) => {
          c.style.whiteSpace = "normal";
          c.style.wordBreak = "break-word";
          c.style.overflowWrap = "anywhere";
        });
        // ResponsiveTable wraps the <table> in a MUI Box with overflowX:auto.
        // Any ancestor of a table that html2canvas would clip needs to be opened up.
        root.querySelectorAll<HTMLElement>("table").forEach((t) => {
          let p: HTMLElement | null = t.parentElement;
          while (p && p !== root) {
            const cs = window.getComputedStyle(p);
            if (cs.overflowX !== "visible") p.style.overflowX = "visible";
            p = p.parentElement;
          }
        });
      };

      const snapshot = async (
        node: HTMLElement,
      ): Promise<HTMLCanvasElement> => {
        document.body.appendChild(node);
        await new Promise((r) => setTimeout(r, 50));
        const canvas = await html2canvas(node, {
          backgroundColor: "#0a1a21",
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
        });
        document.body.removeChild(node);
        return canvas;
      };

      // --- Phase A: header block ---
      const headerWrapper = makeBlockWrapper();
      headerWrapper.style.paddingTop = "60px";
      const headerDiv = document.createElement("div");
      headerDiv.style.textAlign = "center";
      headerDiv.style.paddingBottom = "30px";
      headerDiv.style.borderBottom = "3px solid rgba(226, 114, 91, 0.3)";
      headerDiv.innerHTML = `
        <div style="width: 80px; height: 80px; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center;">
          <img src="/dr-weedy-logo-clean.png" width="80" height="80" style="object-fit: contain;" />
        </div>
        <h1 style="color: #E2725B; font-size: 36px; margin: 0 0 12px 0; font-weight: 700; font-family: Oxanium, sans-serif;">Dr. Weedy</h1>
        <p style="color: rgba(255,255,255,0.8); font-size: 18px; margin: 0 0 8px 0; font-weight: 600;">${
          currentChat?.title || "Consulta Médica"
        }</p>
        <p style="color: rgba(255,255,255,0.5); font-size: 14px; margin: 0;">${new Date().toLocaleDateString(
          "pt-BR",
          {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          },
        )}</p>
      `;
      headerWrapper.appendChild(headerDiv);
      const headerCanvas = await snapshot(headerWrapper);

      // --- Phase B: per-message blocks ---
      const originalChildren = Array.from(
        chatContainerRef.current.children,
      ) as HTMLElement[];
      const messageCanvases: HTMLCanvasElement[] = [];

      for (const child of originalChildren) {
        // Skip transient UI (loaders, error alerts) that aren't chat content.
        if (
          child.querySelector(".MuiCircularProgress-root") ||
          child.classList.contains("MuiAlert-root")
        ) {
          continue;
        }
        const clone = child.cloneNode(true) as HTMLElement;
        clone.style.maxWidth = "100%";
        clone.style.margin = "0";
        applyTableWrapOverrides(clone);

        const blockWrapper = makeBlockWrapper();
        const spacer = document.createElement("div");
        spacer.style.marginBottom = "16px";
        blockWrapper.appendChild(clone);
        blockWrapper.appendChild(spacer);
        messageCanvases.push(await snapshot(blockWrapper));
      }

      // --- Phase C: compose PDF ---
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const blocks = [headerCanvas, ...messageCanvases];
      let cursorY = 0;

      const placeBlock = (block: HTMLCanvasElement) => {
        const blockHeightMm = (block.height * PAGE_W_MM) / block.width;
        const dataUrl = block.toDataURL("image/jpeg", 0.85);

        if (blockHeightMm <= PAGE_H_MM) {
          if (cursorY > 0 && cursorY + blockHeightMm > PAGE_H_MM) {
            pdf.addPage();
            cursorY = 0;
          }
          pdf.addImage(
            dataUrl,
            "JPEG",
            0,
            cursorY,
            PAGE_W_MM,
            blockHeightMm,
            "",
            "FAST",
          );
          cursorY += blockHeightMm;
          return;
        }

        // Fallback: block is taller than a single page. Slice it across pages.
        if (cursorY > 0) {
          pdf.addPage();
          cursorY = 0;
        }
        let remaining = blockHeightMm;
        let offset = 0; // mm of the image already consumed on previous pages
        while (remaining > 0) {
          pdf.addImage(
            dataUrl,
            "JPEG",
            0,
            -offset,
            PAGE_W_MM,
            blockHeightMm,
            "",
            "FAST",
          );
          const consumed = Math.min(PAGE_H_MM, remaining);
          remaining -= consumed;
          offset += consumed;
          if (remaining > 0) {
            pdf.addPage();
          } else {
            cursorY = consumed;
          }
        }
      };

      for (const block of blocks) placeBlock(block);

      pdf.save(`Dr-Weedy-${currentChat?.title || "Consulta"}.pdf`);

      const tempAlert = document.createElement("div");
      tempAlert.className =
        "fixed top-4 right-4 z-50 bg-saluton/90 text-white px-6 py-3 rounded-2xl shadow-xl font-semibold";
      tempAlert.textContent = "PDF exportado!";
      document.body.appendChild(tempAlert);
      setTimeout(() => tempAlert.remove(), 3000);
    } catch (err) {
      console.error("PDF export error:", err);
      setError("Erro ao exportar PDF");
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-petrolio-dark via-[#0a1a21] to-[#04090a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-saluton/15 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-petrolio/10 via-transparent to-transparent"></div>

      <ChatSidebar
        chatSessions={chatSessions.map((session) => ({
          id: session.session_id,
          title: session.title,
          lastMessage: session.first_message || "Nova conversa",
          timestamp: new Date(session.created_at),
        }))}
        currentChatId={currentChatId}
        onNewChat={createNewChat}
        onSelectChat={(chatId) => {
          trackEvent("sidebar_navigation_clicked", {
            destination: "/chat",
            label: "Selecionar conversa",
          });
          selectChat(chatId);
        }}
        onManageSubscription={() => {
          trackEvent("sidebar_navigation_clicked", {
            destination: "/account",
            label: "Minha assinatura",
          });
          navigate("/account");
        }}
        onLogout={handleLogout}
        userEmail={user?.email}
      />

      <div className="flex-1 flex flex-col relative z-10 min-w-0">
        <header className="flex justify-between items-center pl-12 pr-3 py-2 xl:px-8 xl:py-6 bg-gradient-to-r from-petrolio-dark/80 via-petrolio/80 to-petrolio-dark/80 backdrop-blur-xl shadow-2xl border-b border-white/10">
          <div className="flex items-center space-x-3 md:space-x-4 min-w-0">
            <div className="relative hidden xl:block">
              <div className="absolute inset-0 bg-saluton/30 rounded-2xl blur-lg"></div>
              <div className="relative w-12 h-12 flex items-center justify-center">
                <DrWeedyLogo width={48} height={48} />
              </div>
            </div>
            <div className="min-w-0">
              <Typography
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: { xs: "16px", md: "20px" },
                  letterSpacing: "-0.02em",
                  fontFamily: "Oxanium, sans-serif",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span className="truncate">
                  {currentChat?.title || "Dr. Weedy"}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/20 text-success border border-success/30 shrink-0">
                  {loading ? "Carregando..." : "Online"}
                </span>
              </Typography>
              <Typography className="text-white/70 text-sm font-medium mt-0.5 hidden xl:block">
                Assistente com IA para Profissionais da Saúde
              </Typography>
            </div>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 shrink-0">
            <Tooltip title="Compartilhar e Exportar" arrow>
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: "rgba(226, 114, 91, 0.15)",
                    color: "#E2725B",
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              PaperProps={{
                sx: {
                  background:
                    "linear-gradient(135deg, rgba(26, 49, 60, 0.95) 0%, rgba(4, 9, 10, 0.98) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(226, 114, 91, 0.2)",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                  minWidth: "220px",
                },
              }}
            >
              <MenuItem
                onClick={copyMessagesToClipboard}
                disabled={currentMessages.length === 0}
                sx={{
                  color: "#ffffff",
                  padding: "12px 20px",
                  "&:hover": {
                    backgroundColor: "rgba(226, 114, 91, 0.1)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                <ListItemIcon>
                  <ContentCopyIcon sx={{ color: "#E2725B" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Copiar Conversa"
                  primaryTypographyProps={{
                    fontSize: "15px",
                    fontWeight: 600,
                  }}
                />
              </MenuItem>
              <MenuItem
                onClick={exportToPDF}
                disabled={currentMessages.length === 0}
                sx={{
                  color: "#ffffff",
                  padding: "12px 20px",
                  "&:hover": {
                    backgroundColor: "rgba(226, 114, 91, 0.1)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                <ListItemIcon>
                  <PictureAsPdfIcon sx={{ color: "#e16464" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Exportar PDF"
                  primaryTypographyProps={{
                    fontSize: "15px",
                    fontWeight: 600,
                  }}
                />
              </MenuItem>
            </Menu>

            <Tooltip
              title={
                streamingEnabled
                  ? "Streaming ativado - Respostas em tempo real"
                  : "Streaming desativado - Respostas após processamento completo"
              }
              arrow
            >
              <div className="hidden md:flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-2xl px-4 py-2 border border-white/20 shadow-lg">
                <Chip
                  label={streamingEnabled ? "STREAM" : "BATCH"}
                  size="small"
                  className={`text-xs font-bold ${
                    streamingEnabled
                      ? "bg-gradient-to-r from-saluton to-saluton-dark text-white shadow-md"
                      : "bg-gradient-to-r from-dourado to-dourado-dark text-petrolio-dark shadow-md"
                  }`}
                  sx={{
                    height: "24px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    borderRadius: "8px",
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={streamingEnabled}
                      onChange={(e) => setStreamingEnabled(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#E2725B",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#E2725B",
                          },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#f0d763",
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{ margin: 0 }}
                />
              </div>
            </Tooltip>

            <Tooltip title="Sair da conta" arrow>
              <Button
                onClick={handleLogout}
                variant="outlined"
                startIcon={<LogoutIcon />}
                className="hidden md:flex text-white border-coral/50 hover:bg-coral/20 hover:border-coral transition-all duration-300"
                sx={{
                  borderRadius: "16px",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  backdropFilter: "blur(10px)",
                  backgroundColor: "rgba(225, 100, 100, 0.1)",
                  "&:hover": {
                    borderColor: "#e16464",
                    backgroundColor: "rgba(225, 100, 100, 0.2)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(225, 100, 100, 0.3)",
                  },
                }}
              >
                Sair
              </Button>
            </Tooltip>
          </div>
        </header>

        <main
          ref={mainRef}
          onScroll={handleMainScroll}
          className="flex-1 overflow-y-auto overflow-x-hidden p-2 md:p-6 relative"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-petrolio/5 to-petrolio/10"></div>
          <div
            ref={chatContainerRef}
            className="max-w-5xl mx-auto space-y-3 md:space-y-6 relative z-10"
          >
            {error && (
              <Alert
                severity="error"
                className="rounded-2xl backdrop-blur-sm"
                sx={{
                  background: "rgba(225, 100, 100, 0.15)",
                  border: "1px solid rgba(225, 100, 100, 0.3)",
                  color: "#e16464",
                  fontWeight: 500,
                }}
              >
                {error}
              </Alert>
            )}

            {loading && currentMessages.length === 0 && (
              <div className="flex justify-center py-12">
                <CircularProgress size={40} />
              </div>
            )}

            {currentMessages.map((m) => (
              <ChatMessage
                key={m.id}
                message={{
                  id: parseInt(m.id.replace(/\D/g, "") || "0"),
                  text: m.text,
                  from: m.from === "ai" ? "bot" : "user",
                  created_at: m.created_at,
                }}
              />
            ))}

            {isStreaming && streamingMessage && (
              <ChatMessage
                key="streaming"
                message={{
                  id: 999999,
                  text: streamingMessage,
                  from: "bot",
                  created_at: new Date().toISOString(),
                }}
              />
            )}

            {currentMessages.length === 0 && !loading && !isStreaming && (
              <div className="text-center py-8 md:py-16">
                <div className="relative max-w-2xl mx-auto">
                  <div className="absolute inset-0 bg-gradient-to-r from-saluton/20 via-petrolio/20 to-saluton/20 rounded-3xl blur-2xl"></div>
                  <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-6 md:p-12 border border-white/20 shadow-2xl">
                    <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6 flex items-center justify-center">
                      <DrWeedyLogo
                        className="w-16 h-16 md:w-20 md:h-20"
                        width={64}
                        height={64}
                      />
                    </div>
                    <Typography
                      variant="h4"
                      sx={{
                        color: "#ffffff",
                        fontWeight: 700,
                        marginBottom: "16px",
                        letterSpacing: "-0.02em",
                        fontFamily: "Oxanium, sans-serif",
                        fontSize: { xs: "1.5rem", md: "2.125rem" },
                      }}
                    >
                      Como posso ajudá-lo hoje?
                    </Typography>
                    <Typography
                      variant="body1"
                      className="text-white/80 leading-relaxed max-w-lg mx-auto font-medium"
                      sx={{ fontSize: { xs: "14px", md: "16px" } }}
                    >
                      Descreva sintomas e casos específicos ou faça perguntas
                      sobre saúde canábica. Estou aqui para fornecer orientações
                      médicas preliminares baseadas em evidências científicas.
                    </Typography>
                    <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-2 md:gap-3">
                      <div className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 rounded-full border border-white/20 text-white/70 text-xs md:text-sm">
                        Orientação Médica
                      </div>
                      <div className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 rounded-full border border-white/20 text-white/70 text-xs md:text-sm">
                        Baseado em Evidências
                      </div>
                      <div className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 rounded-full border border-white/20 text-white/70 text-xs md:text-sm">
                        Respostas em Tempo Real
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {sendingMessage && !isStreaming && !isPolling && (
              <div className="flex justify-center">
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl px-8 py-4 shadow-lg border border-white/20">
                  <div className="flex items-center space-x-4">
                    <CircularProgress size={24} sx={{ color: "#E2725B" }} />
                    <Typography
                      variant="body2"
                      className="text-white/90 font-medium"
                    >
                      Dr. Weedy está pensando...
                    </Typography>
                  </div>
                </div>
              </div>
            )}

            {isStreaming && (
              <div className="flex justify-center">
                <div
                  className={`backdrop-blur-xl rounded-2xl px-8 py-5 shadow-xl max-w-md ${
                    routeInfo?.route === "agents"
                      ? "bg-gradient-to-r from-[#2d1b69]/30 to-saluton/20 border border-[#7c5cbf]/40"
                      : "bg-gradient-to-r from-saluton/20 to-petrolio/20 border border-saluton/30"
                  }`}
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="flex items-center space-x-2">
                      <div
                        className={`w-3 h-3 rounded-full animate-pulse shadow-lg ${
                          routeInfo?.route === "agents"
                            ? "bg-[#7c5cbf]"
                            : "bg-saluton"
                        }`}
                      ></div>
                      <div
                        className={`w-3 h-3 rounded-full animate-pulse delay-75 shadow-lg ${
                          routeInfo?.route === "agents"
                            ? "bg-[#7c5cbf]"
                            : "bg-saluton"
                        }`}
                      ></div>
                      <div
                        className={`w-3 h-3 rounded-full animate-pulse delay-150 shadow-lg ${
                          routeInfo?.route === "agents"
                            ? "bg-[#7c5cbf]"
                            : "bg-saluton"
                        }`}
                      ></div>
                    </div>
                    <Typography
                      variant="body1"
                      className="text-center text-white font-semibold"
                      data-test='loading-indicator'
                    >
                      {routeInfo?.route === "agents"
                        ? "Dr. Weedy consultando agentes clínicos..."
                        : "Dr. Weedy está digitando..."}
                    </Typography>
                    {reasoning.length > 0 && (
                      <div className="flex flex-col gap-1 text-center">
                        {reasoning.map((linha) => (
                          <Typography
                            key={linha}
                            variant="caption"
                            className="text-white/60 italic"
                          >
                            {linha}
                          </Typography>
                        ))}
                      </div>
                    )}
                    {routeInfo?.route === "agents" &&
                      routeInfo.agentsCalled.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2">
                          {routeInfo.agentsCalled.map((agent) => {
                            const status =
                              routeInfo.agentStatus[agent] || "pending";
                            const statusStyles = {
                              pending: {
                                backgroundColor: "rgba(124, 92, 191, 0.25)",
                                color: "#c4b5fd",
                                border: "1px solid rgba(124, 92, 191, 0.4)",
                              },
                              ok: {
                                backgroundColor: "rgba(74, 222, 128, 0.2)",
                                color: "#86efac",
                                border: "1px solid rgba(74, 222, 128, 0.4)",
                              },
                              warn: {
                                backgroundColor: "rgba(251, 191, 36, 0.2)",
                                color: "#fcd34d",
                                border: "1px solid rgba(251, 191, 36, 0.4)",
                              },
                            } as const;

                            return (
                              <Chip
                                key={agent}
                                label={agent}
                                size="small"
                                icon={
                                  status === "ok" ? (
                                    <CheckCircleIcon
                                      sx={{ fontSize: "14px !important" }}
                                    />
                                  ) : status === "warn" ? (
                                    <ErrorOutlineIcon
                                      sx={{ fontSize: "14px !important" }}
                                    />
                                  ) : undefined
                                }
                                className={
                                  status === "pending" ? "animate-pulse" : ""
                                }
                                sx={{
                                  height: "22px",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  borderRadius: "8px",
                                  transition: "all 0.3s ease",
                                  "& .MuiChip-icon": {
                                    color: "inherit",
                                    marginLeft: "6px",
                                  },
                                  ...statusStyles[status],
                                }}
                              />
                            );
                          })}
                        </div>
                      )}
                    <div
                      className={`px-4 py-1.5 rounded-full border ${
                        routeInfo?.route === "agents"
                          ? "bg-[#7c5cbf]/20 border-[#7c5cbf]/40"
                          : "bg-saluton/20 border-saluton/40"
                      }`}
                    >
                      <Typography
                        variant="caption"
                        className={`text-center font-medium ${
                          routeInfo?.route === "agents"
                            ? "text-[#c4b5fd]"
                            : "text-saluton-light"
                        }`}
                      >
                        {routeInfo?.route === "agents"
                          ? "Analisando com agentes especializados"
                          : "Resposta em tempo real"}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isPolling && (
              <div className="flex justify-center">
                <div className="bg-gradient-to-r from-dourado/20 to-terracota/20 backdrop-blur-xl rounded-2xl px-8 py-5 shadow-xl border border-dourado/30 max-w-md">
                  <div className="flex flex-col items-center space-y-4">
                    <CircularProgress size={32} sx={{ color: "#f0d763" }} />
                    <Typography
                      variant="body1"
                      className="text-center text-white font-semibold"
                    >
                      Dr. Weedy está processando sua consulta...
                    </Typography>
                    <div className="px-4 py-1.5 bg-dourado/20 rounded-full border border-dourado/40">
                      <Typography
                        variant="caption"
                        className="text-center text-dourado-light font-medium"
                      >
                        Aguarde alguns momentos
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </main>

        {isInputCollapsed ? (
          <div
            className="relative px-3 pb-2 pt-1 md:px-6 md:pb-4 md:pt-2"
            style={{ transition: "all 0.3s ease" }}
          >
            <div className="max-w-5xl mx-auto relative z-20">
              <div
                onClick={() => {
                  setIsInputCollapsed(false);
                  scrollToBottom();
                }}
                className="cursor-pointer flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(26, 49, 60, 0.95) 0%, rgba(4, 9, 10, 0.98) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "2px solid rgba(226, 114, 91, 0.25)",
                  borderRadius: "20px",
                  height: "44px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                  transition: "all 0.3s ease",
                }}
              >
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  ↓ Responder
                </Typography>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="relative px-2 pb-2 pt-1 md:px-6 md:pb-6 md:pt-4"
            style={{ transition: "all 0.3s ease" }}
          >
            <div className="max-w-5xl mx-auto relative z-20">
              <ChatInput
                onSend={sendMessage}
                disabled={sendingMessage || isStreaming || isPolling}
                isPolling={isStreaming || isPolling}
                pollingProgress={
                  isStreaming ? 50 : isPolling ? pollingProgress : 0
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
